import React, { useMemo, useState, useEffect, useCallback } from "react";
import IsoWorld from "@/three/IsoWorld";
import TopBar from "@/components/hud/TopBar";
import AreaNav from "@/components/hud/AreaNav";
import InfoPanel from "@/components/hud/InfoPanel";
import { EmbedDialog } from "@/components/EmbedDialog";
import { useBuildings } from "@/lib/useBuildings";
import { findBuilding } from "@/three/helpers";
import { OWL_SHRINE } from "@/three/OwlShrine";
import { getUITheme } from "@/lib/uiTheme";
import { MousePointerClick, Minimize2 } from "lucide-react";

const theme = getUITheme(false);

export default function Home() {
  const { buildings } = useBuildings();
  const [selected, setSelected] = useState(null);
  const [person, setPerson] = useState(null);
  const [immersive, setImmersive] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [embedOpen, setEmbedOpen] = useState(false);

  const sorted = useMemo(
    () => [...buildings].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [buildings]
  );
  // Include the (DB-less) owl shrine so it can show its info panel when focused.
  const current = useMemo(() => findBuilding([...sorted, OWL_SHRINE], selected), [sorted, selected]);

  // Selecting a building (or overview) clears any open citizen-persona panel.
  const handleSelect = useCallback((slug) => {
    setPerson(null);
    setSelected(slug);
  }, []);
  // Clicking a citizen shows their persona without moving the camera.
  const handlePersonSelect = useCallback((p) => setPerson(p), []);
  // The InfoPanel shows a clicked citizen first, otherwise the focused building.
  const panelSubject = person || current;
  const closePanel = useCallback(() => {
    if (person) setPerson(null);
    else setSelected(null);
  }, [person]);

  const enterImmersive = useCallback(async () => {
    setImmersive(true);
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      }
    } catch (e) {
      /* fullscreen may be blocked (e.g. inside an iframe) — UI still hides */
    }
  }, []);

  const exitImmersive = useCallback(async () => {
    setImmersive(false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Keep immersive state in sync when the user leaves native fullscreen (Esc, etc.)
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setImmersive(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setImmersive(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div
      className="relative h-screen w-full overflow-hidden"
      style={{ background: theme.pageBg }}
      data-testid="home-day"
    >
      <IsoWorld buildings={sorted} selected={selected} onSelect={handleSelect} onPersonSelect={handlePersonSelect} showLabels={showLabels && !immersive} />

      {!immersive && (
        <>
          <TopBar onEmbed={() => setEmbedOpen(true)} onFullscreen={enterImmersive} theme={theme} />
          <AreaNav
            buildings={sorted}
            selected={selected}
            onSelect={handleSelect}
            theme={theme}
            collapsed={navCollapsed}
            onToggleCollapse={() => setNavCollapsed((v) => !v)}
            showLabels={showLabels}
            onToggleLabels={() => setShowLabels((v) => !v)}
          />
          <InfoPanel building={panelSubject} onClose={closePanel} theme={theme} />
          <div className="pointer-events-none absolute right-3 md:right-6 bottom-6 z-20 hidden lg:flex items-center gap-2 rounded-full px-3 py-1.5" style={{ background: theme.chipBg, boxShadow: "var(--shadow-panel)" }}>
            <MousePointerClick size={14} style={{ color: theme.textStrong }} />
            <span className="font-display uppercase tracking-wide text-[11px]" style={{ color: theme.textStrong }}>Click a building to fly in</span>
          </div>
        </>
      )}

      {immersive && (
        <button
          data-testid="exit-fullscreen-button"
          onClick={exitImmersive}
          className="absolute right-4 top-4 z-50 inline-flex items-center gap-2 rounded-[10px] px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide text-white active:scale-[0.98] transition-colors"
          style={{ background: "rgba(43,46,52,0.82)", boxShadow: "var(--shadow-panel)", backdropFilter: "blur(8px)" }}
        >
          <Minimize2 size={16} /> Exit
        </button>
      )}

      <EmbedDialog open={embedOpen} onOpenChange={setEmbedOpen} />
    </div>
  );
}
